/**
 * PortalInstallBanner (Reppr Elite, phase 7) — light theme.
 *
 * Prompts athletes to add the portal to their home screen.
 * - iOS Safari: step-by-step manual instructions
 * - Android Chrome: native install prompt via beforeinstallprompt
 *
 * Appears 5 seconds after first portal visit.
 * Dismissed state persisted in localStorage.
 */

import { useState, useEffect } from 'react'
import { useInstallPrompt } from '../hooks/useInstallPrompt'
import styles from './PortalInstallBanner.module.css'

const DISMISSED_KEY = 'portal_install_dismissed'
const LATER_KEY = 'portal_install_later_until'
const LATER_DAYS = 7

const IOS_STEPS = [
  'Tap the Share button at the bottom of Safari',
  'Scroll down and tap "Add to Home Screen"',
  'Tap "Add" in the top right corner',
]

function isAlreadyInstalled(): boolean {
  if (typeof window === 'undefined') return true
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  )
}

function isIOS(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

function isSafari(): boolean {
  return /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent)
}

interface PortalInstallBannerProps {
  agencyName?: string | null
  agencyLogo?: string | null
}

export default function PortalInstallBanner({ agencyName, agencyLogo }: PortalInstallBannerProps) {
  const [visible, setVisible] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  const { canInstall, promptInstall, installed } = useInstallPrompt()

  const ios = isIOS()
  const safari = isSafari()

  useEffect(() => {
    // Don't show if already installed
    if (isAlreadyInstalled()) return
    // Don't show if permanently dismissed
    if (localStorage.getItem(DISMISSED_KEY)) return
    // Don't show if "later" hasn't expired
    const laterUntil = localStorage.getItem(LATER_KEY)
    if (laterUntil && Date.now() < parseInt(laterUntil)) return

    // iOS: only useful in Safari (Web Push + home screen only works in Safari PWA on iOS)
    if (ios && !safari) return

    // Show after 5 seconds
    const t = setTimeout(() => setVisible(true), 5000)
    return () => clearTimeout(t)
  }, [ios, safari])

  // Hide once installed via native prompt
  useEffect(() => {
    if (installed) setVisible(false)
  }, [installed])

  if (!visible) return null

  // Only show for iOS or Android with install capability
  if (!ios && !canInstall) return null

  function dismiss(permanently: boolean) {
    if (permanently) {
      localStorage.setItem(DISMISSED_KEY, '1')
    } else {
      localStorage.setItem(LATER_KEY, String(Date.now() + LATER_DAYS * 86_400_000))
    }
    setVisible(false)
  }

  async function handleAndroidInstall() {
    await promptInstall()
    dismiss(true)
  }

  return (
    <div className={styles.banner}>
      <div className={styles.row}>
        <div className={styles.mark}>
          {agencyLogo && <img src={agencyLogo} alt="" width={40} height={40} />}
        </div>

        <div className={styles.text}>
          <p className={styles.title}>Add to your home screen</p>
          <p className={styles.sub}>
            {ios
              ? 'Open in Safari and tap Share to install'
              : `Install ${agencyName || 'the portal'} for faster access`}
          </p>
        </div>

        <div className={styles.actions}>
          {ios ? (
            <button onClick={() => setShowSteps(!showSteps)} className={styles.primary}>
              How?
            </button>
          ) : (
            <button onClick={handleAndroidInstall} className={styles.primary}>
              Install
            </button>
          )}
          <button onClick={() => dismiss(false)} className={styles.secondary}>
            Later
          </button>
        </div>
      </div>

      {ios && showSteps && (
        <div className={styles.steps}>
          <p className={styles.stepsTitle}>Add to home screen — Safari</p>
          {IOS_STEPS.map((text, i) => (
            <div key={text} className={styles.step}>
              <span className={styles.stepNum}>{i + 1}</span>
              <span className={styles.stepText}>{text}</span>
            </div>
          ))}
          <button onClick={() => dismiss(true)} className={styles.stepsDismiss}>
            Got it, don't show again
          </button>
        </div>
      )}
    </div>
  )
}
